import { toast } from "react-toastify";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import FieldDatepicker from "components/forms/fields/FieldDatepicker";

import {
  workScheduleOptions,
  workDayOptions,
  missionTypeOptions,
  workTimeOptions,
} from "../constants";
import FieldSwitch from "components/forms/fields/switch";
import FieldAutocompleteList from "components/forms/fields/FieldAutocompleteList";
import FieldAutocompleteMultiSelect from "components/forms/fields/FieldAutocompleteMultiSelect";
import FieldToggleButtonGroup from "components/forms/fields/FieldToggleButtonGroup";
import { useGetValueOptions } from "core/value_options/hooks";
import FieldSlider from "components/forms/fields/FieldSlider";
import { GridBreak } from "components/GridBreak";
import { Button } from "components/buttons/button";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { useMemo } from "react";
import { ArchiveButton } from "core/archive";

type PreferenceFormProps = {
  tempWorker: any;
};

const PreferenceForm = ({ tempWorker }: PreferenceFormProps) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<any>();
  const hasMedicalVisit = watch("has_medical_visit");
  const { fields, append } = useFieldArray({
    control: control,
    name: "qualifications",
    rules: { minLength: 0 },
  });
  const {
    data: valueOptions,
    isLoading,
    isError,
  } = useGetValueOptions({
    namespace: "anael",
    category: "qualification",
    onError: (err) => toast.error("une erreur est survenue"),
  });
  const qualificationOptions = useMemo(
    () =>
      valueOptions?.options?.reduce((a: any, v: any) => {
        a.push([v.value, v.label]);
        return a;
      }, []) || [],
    [valueOptions],
  );

  if (isLoading) {
    return <CircularProgress size={20} />;
  }
  if (!isLoading && (isError || valueOptions === undefined)) {
    return <Typography color={"red"}>Une erreur est survenue.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h5">Déplacement</Typography>
      <Grid container spacing={2} columns={{ xs: 1, sm: 2 }}>
        <Grid item xs={1}>
          {/* <FieldSwitch
                        control={control}
                        fieldName='driver_license'
                        label='Permis B'
                    /> */}
          <FieldAutocompleteList
            control={control}
            options={[
              [true, "Oui"],
              [false, "Non"],
            ]}
            fieldName="driver_license"
            labelId="driver_license"
            label="Permis de conduire"
          />
        </Grid>
        <Grid item xs={1}>
          {/* <FieldSwitch
                        control={control}
                        fieldName='vehicule_owner'
                        label='Véhiculé'
                    /> */}
          <FieldAutocompleteList
            control={control}
            options={[
              [true, "Oui"],
              [false, "Non"],
            ]}
            fieldName="vehicule_owner"
            labelId="vehicule_owner"
            label="Véhiculé"
          />
        </Grid>
        <Grid item xs={1} sm={2}>
          <FieldAutocompleteMultiSelect
            control={control}
            options={[
              ["b", "Permis B"],
              ["c", "Permis C"],
              ["ce", "Permis CE"],
              ["d", "Permis D"],
            ]}
            fieldName="driver_license_types"
            labelId="driver_license_types"
            label="Catégories de permis possédées"
          />
        </Grid>
        <Grid item xs={1}>
          <FieldSlider
            control={control}
            fieldName="default_search_range"
            label="Distance maximale de la mission"
            max={40}
          />
          {/* <TextField
                        required
                        id='default_search_range'
                        label='Distance (en km)'
                        {...register('default_search_range')}
                        autoComplete='off'
                        error={errors?.default_search_range ? true : false}
                        helperText={errors?.default_search_range ? errors.default_search_range.message as string : null}
                    /> */}
        </Grid>
      </Grid>
      <Divider sx={{ my: "1rem" }} />
      <Typography variant="h5">Qualifications</Typography>
      <Grid container spacing={2} columns={{ xs: 4, sm: 9 }}>
        <Grid item xs={3}>
          <FieldAutocompleteList
            control={control}
            options={qualificationOptions}
            fieldName="qualification"
            labelId="qualification"
            label="Qualification principale"
          />
        </Grid>
        <GridBreak />
        {fields.map((item, index) => (
          <Grid item xs={3} key={item.id}>
            <FieldAutocompleteList
              key={item.id}
              control={control}
              options={qualificationOptions}
              fieldName={`qualifications[${index}]`}
              labelId={`qualifications-${index}`}
              label="Qualification secondaire"
            />
          </Grid>
        ))}
        <Grid item xs={2}>
          <Button
            variant="outlined"
            size="small"
            color="info"
            sx={{ mt: "1rem" }}
            onClick={() => append(undefined)}
            disabled={isLoading}
          >
            <AddCircleOutlineOutlined sx={{ mr: "8px" }} />
            Ajouter
          </Button>
        </Grid>
      </Grid>
      <Divider sx={{ my: "1rem" }} />
      <Typography variant="h5">Formations</Typography>
      <Grid container spacing={2} columns={{ xs: 1, sm: 3 }}>
        <Grid item xs={1}>
          <FieldAutocompleteList
            control={control}
            options={[
              [true, "Oui"],
              [false, "Non"],
            ]}
            fieldName="interested_in_training"
            labelId="interested_in_training"
            label="Intéressés par une formation?"
          />
        </Grid>
        <Grid item xs={1} sm={2}>
          <TextField
            id="training_free_form"
            label="Types de formation"
            {...register("training_free_form")}
            autoComplete="off"
            error={errors?.training_free_form !== undefined}
            helperText={
              errors?.training_free_form &&
              (errors.training_free_form.message as string)
            }
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: "1rem" }} />
      <Typography variant="h5">Médical</Typography>
      <Grid container spacing={2} columns={{ xs: 1, sm: 3 }}>
        <Grid item xs={1}>
          <FieldAutocompleteList
            control={control}
            options={[
              [true, "Oui"],
              [false, "Non"],
            ]}
            fieldName="has_medical_visit"
            labelId="has_medical_visit"
            label="A réalisé une visite médicale"
          />
        </Grid>
        {hasMedicalVisit === true && (
          <Grid item xs={1}>
            <FieldDatepicker
              control={control}
              label="Date de la dernière visite"
              fieldName="last_medical_visit_date"
            />
          </Grid>
        )}
      </Grid>
      <Divider sx={{ my: "1rem" }} />
      <Typography variant="h5">Horaires</Typography>
      <FieldSwitch control={control} fieldName="available" label="Disponible" />
      <FieldToggleButtonGroup
        control={control}
        fieldName="mission_type"
        options={missionTypeOptions}
        labelId="mission_type"
        label="Type de mission"
        exclusive={false}
      />
      <FieldToggleButtonGroup
        control={control}
        fieldName="work_schedule"
        options={workScheduleOptions}
        labelId="work_schedule"
        label="Horaires"
        exclusive={false}
      />
      <FieldToggleButtonGroup
        control={control}
        fieldName="work_time"
        options={workTimeOptions}
        labelId="work_time"
        label="Type de contrat"
        exclusive={false}
      />
      <FieldToggleButtonGroup
        control={control}
        fieldName="work_day"
        options={workDayOptions}
        labelId="work_day"
        label="Jours"
        exclusive={false}
      />
      <Box
        sx={{
          marginTop: "1rem",
        }}
      >
        <ArchiveButton tempWorker={tempWorker} />
      </Box>
    </Box>
  );
};

export default PreferenceForm;
